import { Injectable } from '@angular/core';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import {
    AssessmentSiteTypes,
    BehavioralAssessmentStatus,
    LegacyGender
} from '../../constants-types/Enums';
import { PI } from '../../constants-types/PI.model';
import {
    BehavioralReportConfiguration,
    ReferencePatternInformationServer,
    ReferencePatternsInformation,
    User,
    Person
} from '../../core-data-model';
import {
    BAInvitationQueryParam,
    InvitationSource
} from '../../core-data-model/Assessment.model';
import { Company } from '../../core-data-model/Company.model';
import {
    PISignalRMessage,
    PISignalRMessageData,
    MatchIdentityDataService,
    SignalRService
} from '../../core-services';
import { CompaniesDataService } from '../../core-services/Companies.dataservice';
import { PersonApi } from '../../core-services/PersonApi.connect';
import { StringUtilities } from '../../core-utilities';
import { PdfReference } from '../../system-services';
import { InitDataService } from '../../system-services/InitData/InitData.service';
import { Logger } from '../../system-services/Logger/Logger.service';
import { ModuleAccessService } from '../../system-services/ModuleAccess/ModuleAccess.service';
import { TranslationService } from '../../translations';

class BehavioralAssessmentNotificationDataCto implements PISignalRMessageData {
    companyId: string;
    surveyUserId: string;
    administeredById: string;
    assessmentId: string;
    referencePatternInformation: ReferencePatternInformationServer;
}

class BACompleteMessage
    implements PISignalRMessage<BehavioralAssessmentNotificationDataCto>
{
    data: BehavioralAssessmentNotificationDataCto;
    eventTarget = 'BAComplete';
}

@Injectable({ providedIn: 'root' })
export class MyProfileTooltipService {
    person: Person;
    assessmentState: BehavioralAssessmentStatus =
        BehavioralAssessmentStatus.Unknown;
    BALink: string;
    // Thinking this should be somewhere else if we have other components that will need to use this.
    baPollingKey = 'SelfBaCompletion';
    currentCompany: Company;
    showProfile = false;
    assessmentId: string;
    assessmentUrl: string;

    private personFetchPromise: Promise<Person[]>;
    private signalrSubscription: Subscription;
    private showProfileSubject = new Subject<boolean>();
    showProfile$ = this.showProfileSubject.asObservable();

    private assessmentCompletedSubject = new Subject<boolean>();
    assessmentCompleted$ = this.assessmentCompletedSubject.asObservable();

    constructor(
        private initData: InitDataService,
        private personApi: PersonApi,
        private companyService: CompaniesDataService,
        private moduleAccess: ModuleAccessService,
        private logger: Logger,
        private signalRService: SignalRService,
        private stringUtil: StringUtilities,
        public TR: TranslationService,
        private matchIdentityService: MatchIdentityDataService
    ) {}

    public setAssessmentInformation(assessment: { id: string; url: string }) {
        this.assessmentId = assessment.id;
        this.assessmentUrl = assessment.url;
    }

    async determineAssessmentState() {
        this.currentCompany =
            await this.companyService.getCurrentLoggedInCompany();

        // Initial setting, this will update based on value of this.person
        this.assessmentState = BehavioralAssessmentStatus.Pending;

        this.person = await firstValueFrom(
            this.matchIdentityService.matchedPersonForUser$
        );

        if (this.person) {
            if (this.person.hasBehavioralPattern()) {
                // Completed state logic
                this.assessmentState = BehavioralAssessmentStatus.Completed;
                // Build logic to fetch the person snapshot info
            } else {
                // pending state logic
                this.hydratePendingState();
            }
        } else {
            this.assessmentState = BehavioralAssessmentStatus.Unknown;
        }
    }

    /**
     * If many calls are made to this, it can generate unecessary API calls, instead we should just return the person we already have
     * and if not then return the promise to get the person.
     * @deprecated This call is dependent on Classic PeopleAndJobs call. Avoid usage in PI2
     */
    async findPerson(): Promise<Person> {
        if (this.person) {
            return this.person;
        }

        if (!this.initData.user.surveyUserId) {
            return null;
        }

        if (!this.personFetchPromise) {
            this.personFetchPromise = this.personApi.getPersonDetails([
                this.initData.user.surveyUserId
            ]);
        }

        return this.personFetchPromise.then((personCollection) => {
            this.person = personCollection[0];
            return this.person;
        });
    }

    getInvitationSource(): InvitationSource {
        // TODO: We need to figure out if we need a common InvitationSource enum or we can use Hire for now until that decision. Rich will followup on this matter with Hire team.
        return InvitationSource.Hire;
    }

    getInvitationQueryParam() {
        // TODO: We need to figure out what happens when we have combinations
        if (this.moduleAccess.hasHire || this.moduleAccess.hasHireHero) {
            return BAInvitationQueryParam.Hire;
        }
    }

    async createAndSubscribeToPoll(showTooltip = true) {
        this.assessmentState = BehavioralAssessmentStatus.Pending;
        await this.findPerson();

        // Open the tooltip only if it's closed
        this.setTooltipVisibility(showTooltip);

        if (this.signalrSubscription) {
            return;
        }
        this.signalRService.connect();
        this.signalrSubscription = this.signalRService
            .listenFor<
                BACompleteMessage,
                BehavioralAssessmentNotificationDataCto
            >(BACompleteMessage)
            .subscribe((data) => {
                if (this.person.id === data.surveyUserId) {
                    this.person.referencePattern =
                        ReferencePatternsInformation.find(
                            (ref) =>
                                ref.id ===
                                data.referencePatternInformation.patternNumber
                        );
                    this.assessmentState = BehavioralAssessmentStatus.Completed;
                    this.assessmentCompletedSubject.next(true);
                    this.signalrSubscription.unsubscribe();
                    this.signalrSubscription = null;
                }
            });
    }

    pollPerson(): Promise<Person[]> {
        return this.personApi.getPersonDetails([
            this.initData.user.surveyUserId
        ]);
    }

    setTooltipVisibility(isOpen: boolean) {
        this.showProfile = isOpen;
        this.showProfileSubject.next(this.showProfile);
    }

    toggleVisibility() {
        this.showProfile = !this.showProfile;
        this.showProfileSubject.next(this.showProfile);
    }

    downloadLoggedInUserBAReport() {
        // To print the BA report of the logged in User
        const personToReport = [
            {
                personId: this.initData.user.surveyUserId,
                gender: LegacyGender[LegacyGender.Unknown]
            }
        ];
        this.initiateDownload(
            personToReport,
            this.initData.user.firstName,
            this.initData.user.lastName,
            this.initData.user.userGuid
        );
    }

    async downloadReport(user: User) {
        // To print the BA report of any user
        const userConfig = [
            {
                personId: user.personId, // surveyUserId
                gender: LegacyGender[LegacyGender.Unknown]
            }
        ];

        this.initiateDownload(
            userConfig,
            user.firstName,
            user.lastName,
            user.personId
        );
    }

    private async initiateDownload(
        userConfig: { personId: string; gender: string }[],
        firstName: string,
        lastName: string,
        userId: string
    ) {
        const baConfig = new BehavioralReportConfiguration({
            isManagement: true,
            isInfluencing: true,
            isSelling: false,
            languageLocale: this.TR.currentLocale
        });

        try {
            const pdfReference: PdfReference =
                await this.personApi.getBAReportBulk(userConfig, baConfig);
            pdfReference.download(this.generateReportName(firstName, lastName));
        } catch (error) {
            this.logger.logError('Download behavioral report failed', {
                error: error
            });
            throw error;
        } finally {
            this.logReportDownload(userId);
        }
    }

    private async hydratePendingState() {
        this.assessmentId = this.extractAssessmentId();

        if (this.assessmentId) {
            // get the assessment Url
            this.assessmentUrl = await this.getBaLink(
                this.assessmentId,
                this.getInvitationQueryParam()
            );
            await this.createAndSubscribeToPoll();
        } else {
            // we don't have any pending assessment, mark the state as unknown
            this.assessmentState = BehavioralAssessmentStatus.Unknown;
        }
    }

    private extractAssessmentId() {
        return this.person.behavioralAssessments.find(
            (x) => x.assessmentState === 10 // Pending state.
        )?.id;
    }

    private async getBaLink(
        assessmentId: string,
        invitationQueryParam: BAInvitationQueryParam
    ) {
        const param = invitationQueryParam ? `p=${invitationQueryParam}` : '';
        return `${PI.appSettings.assessmentSite}/v1/${this.currentCompany?.id}/${assessmentId}?type=${AssessmentSiteTypes.EmailInviteBehavioral}&${param}`;
    }

    private generateReportName(firstName: string, lastName: string) {
        return this.stringUtil.formatString(
            this.TR.key('behavioralAssessmentDownload.fileName'),
            lastName,
            firstName
        );
    }

    private logReportDownload(userId: string) {
        this.logger.logInfoNoReturn('Behavioral_Report_Download', {
            event: 'ThirdPartyBAReportButtonClicked',
            page: 'GlobalNav',
            subPage: 'MyProfileTooltip',
            companyId: this.initData.user.companyId,
            data: {
                userId: userId
            }
        });
    }

    refreshProfileData(): Promise<void> {
        this.matchIdentityService.clearMatchedPerson();
        return this.determineAssessmentState();
    }
}
